import { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * Compact 7-day trend sparkline used inside the Dashboard score panels.
 *
 * Renders a smooth polyline + start/end dots on a fixed viewBox so the SVG
 * scales fluidly. Zero-valued days (no meals logged) are drawn as gaps to
 * avoid punishing off-days visually.
 */
export function Sparkline({ series = [], color = "#5A8B8F", width = 120, height = 32, className = "" }) {
  const { path, dots, minY, maxY } = useMemo(() => {
    if (!series || series.length < 2) return { path: "", dots: [], minY: 0, maxY: 100 };
    // Treat 0 as "no data" gap to avoid a spike-down for off-days.
    const nonZero = series.filter((v) => v > 0);
    if (nonZero.length < 2) return { path: "", dots: [], minY: 0, maxY: 100 };
    const min = Math.max(0, Math.min(...nonZero) - 5);
    const max = Math.min(100, Math.max(...nonZero) + 5);
    const range = Math.max(1, max - min);
    const step = width / (series.length - 1);
    let d = "";
    let firstX = null, firstY = null, lastX = null, lastY = null;
    series.forEach((v, i) => {
      if (v <= 0) return; // skip gap
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      if (d === "") {
        d = `M ${x.toFixed(1)} ${y.toFixed(1)}`;
        firstX = x; firstY = y;
      } else {
        d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
      }
      lastX = x; lastY = y;
    });
    return {
      path: d,
      dots: [
        firstX !== null ? { x: firstX, y: firstY, opacity: 0.5 } : null,
        lastX !== null ? { x: lastX, y: lastY, opacity: 1 } : null,
      ].filter(Boolean),
      minY: min,
      maxY: max,
    };
  }, [series, width, height]);

  if (!path) {
    return (
      <span className={`inline-flex items-center text-[10px] text-slate-400 italic ${className}`}>
        —
      </span>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={`overflow-visible ${className}`}
      role="img"
      aria-label="trend"
    >
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={i === dots.length - 1 ? 3 : 2} fill={color} opacity={d.opacity} />
      ))}
    </svg>
  );
}

/**
 * Full trend banner shown at the top of an expanded score panel:
 * label · sparkline · arrow + delta pill.
 */
export function TrendBanner({ label, series, delta, color = "#5A8B8F", days = 7, testId }) {
  const positive = (delta ?? 0) > 0;
  const negative = (delta ?? 0) < 0;
  const flat = !positive && !negative;
  const Arrow = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const arrowCls = positive
    ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300"
    : negative
      ? "text-rose-600 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-300"
      : "text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-300";
  const deltaTxt = flat ? "±0" : `${positive ? "+" : ""}${delta}`;

  return (
    <div
      className="flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 px-2.5 py-1.5"
      data-testid={testId}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 leading-tight">
          {label} · {days}d
        </p>
        <Sparkline series={series || []} color={color} width={110} height={22} className="mt-0.5" />
      </div>
      <span
        className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums shrink-0 ${arrowCls}`}
        data-testid={testId ? `${testId}-delta` : undefined}
      >
        <Arrow className="h-3 w-3" />
        {deltaTxt}
      </span>
    </div>
  );
}
